import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthContext } from './AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.insightos.co.uk';

// Routes that don't require a session; auth failures here should never trigger a redirect.
const PUBLIC_PATH_PREFIXES = [
  '/leads/login', '/login',
  '/leads/mfa', '/mfa',
  '/leads/forgotpassword', '/forgotpassword',
  '/tos', '/tosstatic',
  '/qualification', '/unsubscribe',
  '/leads/logout', '/logout',
];

function isPublicPath(pathname) {
  return pathname === '/' || PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);

  const refreshAuth = useCallback(async () => {
    setIsVerifying(true);
    try {
      // On cross-site origins (avenorai.co.uk), Safari ITP blocks the leads_jwt
      // cookie. Fall back to a token stored in sessionStorage after MFA verify.
      const storedToken = typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('leads_auth_token')
        : null;
      const headers = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};

      const response = await fetch(`${API_BASE}/leads/auth`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });

      const contentType = String(response.headers.get('content-type') || '').toLowerCase();
      const rawBody = await response.text();
      const data = rawBody && contentType.includes('application/json')
        ? JSON.parse(rawBody)
        : { success: false, message: rawBody ? 'Invalid auth response from server' : 'No response body returned' };

      if (response.ok && data?.success) {
        setUser(data.user || null);
        return { success: true, user: data.user || null };
      }

      setUser(null);
      // No valid token/session for this request — hard-redirect straight to /leads/login
      // (unless we're already on a page that doesn't require auth).
      if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
        window.location.href = '/leads/login';
      }
      return { success: false, user: null, message: data?.message || 'Not authenticated' };
    } catch (error) {
      console.error('Auth verification failed:', error);
      setUser(null);
      return { success: false, user: null, message: 'Auth verification failed' };
    } finally {
      setIsVerifying(false);
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const logout = useCallback(async () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('leads_auth_token');
    }
    setUser(null);
    setIsVerifying(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isVerifying,
      refreshAuth,
      setUser,
      logout,
    }),
    [user, isVerifying, refreshAuth, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
